import React, { useState, useRef } from 'react'
import { graphql, navigate } from 'gatsby'
import useQueryParam from '../hooks/useQueryParam'
import InPortal from '../utils/in-portal'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'

interface Props {
  portalRef?: string
  allowRedirect?: boolean
  formPageSettings: any
  form: any
}

export const GRAVITY_FORM_FIELDS = graphql`
  fragment GravityFormFields on WpGfForm {
    confirmations {
      url
      message
      type
    }
    formFields {
      nodes {
        databaseId
        type
        ... on WpTextField {
          label
          isRequired
          inputName
          placeholder
          value
        }
        ... on WpSelectField {
          label
          isRequired
          inputName
          placeholder
          choices {
            text
            value
          }
        }
        ... on WpHiddenField {
          label
          inputName
          value
        }
        ... on WpEmailField {
          label
          isRequired
          inputName
          value
        }
        ... on WpPhoneField {
          databaseId
          label
          isRequired
          inputName
          type
          placeholder
          value
        }
      }
    }
  }
`

const GravityForm: React.FC<Props> = ({
  portalRef = 'gForm',
  allowRedirect = true,
  formPageSettings,
  form,
}) => {
  const [hasErrors, setHasErrors] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [honeyPot, setHoneyPot] = useState(false)

  const {
    confirmations,
    formFields: { nodes },
  } = form

  const {
    redirectUrl,
    pardotFormHandlerUrl,
    gravityFormLabelCssClasses,
    gravityFormInputCssClasses,
    gravityFormButtonCssClasses,
    gravityFormButtonText,
  } = formPageSettings

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = useForm()

  const formRef = useRef(null)

  enum confirmationTypes {
    REDIRECT = 'REDIRECT',
    MESSAGE = 'MESSAGE',
  }

  const formConfirmationType = confirmations[0].type ? confirmations[0]?.type : ''
  const formConfirmationMessage = confirmations[0]?.message ? confirmations[0]?.message : ''
  const formConfirmationUrl = redirectUrl ? redirectUrl : ''

  const formHandlerUrl = pardotFormHandlerUrl ? pardotFormHandlerUrl : ''

  const labelClasses = gravityFormLabelCssClasses ? gravityFormLabelCssClasses : ''
  const inputClasses = gravityFormInputCssClasses ? gravityFormInputCssClasses : ''
  const buttonClasses = gravityFormButtonCssClasses ? gravityFormButtonCssClasses : ''
  const buttonText = gravityFormButtonText ? gravityFormButtonText : 'Submit'

  const requiredMessage = 'Please complete this required field.'

  const formVariant = {
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
    hidden: {
      opacity: 0,
      transition: {
        when: 'afterChildren',
      },
    },
  }

  /* Handle framer variants */

  const sectionVariant = {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
  }

  const getQueryParam = (param = '') => {
    const [sourceQuery, setSourceQuery] = useQueryParam(param, '')

    return sourceQuery
  }

  const onSubmitForm = async (data) => {
    const urlParams = new URLSearchParams()

    for (let name in data) {
      urlParams.append(name, data[name])
    }

    try {
      await fetch(formHandlerUrl, {
        method: 'post',
        mode: 'no-cors',
        cache: 'no-cache',
        body: urlParams.toString(),
      })

      if (formConfirmationType === confirmationTypes.REDIRECT && formConfirmationUrl) {
        navigate(formConfirmationUrl, { state: { email: data?.email } })
      }

      if (formConfirmationType === confirmationTypes.MESSAGE) {
        navigate(`?pi_list_email=${data?.email}`, { state: { email: data?.email } })
        setHasSubmitted(true)
      }
    } catch (err) {
      setHasErrors(true)
      console.log('err', err)
    }
  }

  return (
    <InPortal id={portalRef}>
      <motion.div variants={formVariant} initial='hidden' animate='visible'>
        {hasErrors ? 'There was a problem submitting your form.' : ''}
        {!hasSubmitted ? (
          <form
            acceptCharset='UTF-8'
            ref={formRef}
            method='post'
            action={formHandlerUrl}
            onSubmit={handleSubmit(async (data) => {
              if (honeyPot === true) {
                return true
              } else {
                await onSubmitForm(data)
              }
            })}
          >
            {nodes.map((field) => {
              if (field.type === 'HIDDEN') {
                return (
                  <input
                    key={field?.databaseId}
                    {...register(`${field?.inputName}`)}
                    id={field?.inputName}
                    type={field?.type}
                    name={field?.inputName}
                    value={getQueryParam(field?.inputName)}
                  />
                )
              } else if (field.type === 'SELECT') {
                return (
                  <motion.div key={field?.databaseId} variants={sectionVariant} className='mb-4'>
                    <label htmlFor={field?.inputName} className={`${labelClasses}`}>
                      {field?.label}
                    </label>
                    <select
                      {...register(`${field?.inputName}`, { required: field.isRequired })}
                      id={field?.inputName}
                      className={`transition-opacity duration-300 ${inputClasses}
              ${isSubmitting ? 'opacity-75' : ''}
              `}
                      name={field?.inputName}
                      disabled={isSubmitting}
                    >
                      <option value=''>Select a {field?.label}...</option>
                      {field?.choices.map((choice) => {
                        return (
                          <option key={choice.value} value={choice.value}>
                            {choice.text}
                          </option>
                        )
                      })}
                    </select>
                    {errors[`${field?.inputName}`] && (
                      <p className='body-4 text-gray-black mt-2'>{requiredMessage}</p>
                    )}
                  </motion.div>
                )
              } else {
                return (
                  <motion.div key={field?.databaseId} variants={sectionVariant} className='mb-4'>
                    <label htmlFor={field?.inputName} className={`${labelClasses}`}>
                      {field?.label}
                    </label>
                    <input
                      {...register(`${field?.inputName}`, { required: field.isRequired })}
                      id={field?.inputName}
                      type={field?.type}
                      className={`transition-opacity duration-300 ${inputClasses}
              ${isSubmitting ? 'opacity-75' : ''}
              `}
                      name={field?.inputName}
                      disabled={isSubmitting}
                    />
                    {errors[`${field?.inputName}`] && (
                      <p className='body-4 text-gray-black mt-2'>{requiredMessage}</p>
                    )}
                  </motion.div>
                )
              }
            })}
            <input
              type='text'
              className='opacity-0 absolute top-0 left-0 bottom-0 right-0 h-0 w-0 z-n1'
              name='a_password'
              tabIndex={-1}
              autoComplete='false'
              onChange={() => setHoneyPot(true)}
            />
            <motion.div variants={sectionVariant}>
              <input
                type='submit'
                value={`${isSubmitting ? 'Sending...' : buttonText}`}
                className={`transition-color transition-opacity duration-300 ${buttonClasses} ${
                  isSubmitting ? 'opacity-75' : ''
                } `}
                disabled={isSubmitting}
              />
            </motion.div>
          </form>
        ) : (
          <div key='body' className='body-2'>
            {formConfirmationMessage ? formConfirmationMessage : 'Your form has been submitted'}
          </div>
        )}
      </motion.div>
    </InPortal>
  )
}

export default GravityForm
